import { createMediaItemMutation } from '@/graphql/mutations/Media.mutation';
import type { MediaItem } from '@/graphql/types';
import type Konva from 'konva';
import { toast } from 'react-toastify';
import { useCanvasStore } from '../store/canvasStore';
import type { AnimationType, CanvasImage, LogoProps, TextItem } from '../types';

export interface CanvasMediaItemInput {
  id?: string;
  mediaItemUrl?: string;
  title?: string | null;
  description?: string;
  format?: string;
}

import { exportCanvasGif } from './canvas';

interface CanvasState {
  version: string;
  backgroundColor: string;
  bgImage: string | null;
  texts: TextItem[];
  images: CanvasImage[];
  logoImage: string | null;
  logoProps: LogoProps | null;
}

const dataURLtoFile = (dataurl: string, filename: string): File | null => {
  if (!dataurl?.startsWith('data:image/')) return null;

  try {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);

    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }

    return new File([u8arr], filename, { type: mime });
  } catch {
    return null;
  }
};

const serializeCanvasState = (): string => {
  const state = useCanvasStore.getState();

  const serializedTexts = state.texts.map(
    ({ id, text, x, y, width, color, fontFamily, fontSize, lineHeight, letterSpacing, align, animation, zIndex }) => ({
      id,
      text,
      x,
      y,
      width,
      color,
      fontFamily,
      fontSize,
      lineHeight,
      letterSpacing,
      align,
      animation,
      zIndex: zIndex || 1,
    })
  );

  const serializedImages = state.canvasImages.map(
    ({ id, url, x, y, width, height, opacity, rotation, animation, zIndex }) => ({
      id,
      url,
      x,
      y,
      width,
      height,
      opacity: opacity || 1,
      rotation: rotation || 0,
      animation: animation || 'none',
      zIndex: zIndex || 1,
    })
  );

  return JSON.stringify({
    version: '1.0',
    backgroundColor: state.backgroundColor,
    bgImage: state.bgImage,
    texts: serializedTexts,
    images: serializedImages,
    logoImage: state.logoImage,
    logoProps: state.logoProps ? { ...state.logoProps } : null,
  });
};

const generateCanvasGif = async (stage: Konva.Stage): Promise<Blob> => {
  return new Promise<Blob>((resolve, reject) => {
    try {
      exportCanvasGif(stage, false, (blob: Blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to generate GIF'));
        }
      });
    } catch (error) {
      reject(error);
    }
  });
};

const hasCanvasAnimations = (state: ReturnType<typeof useCanvasStore.getState>): boolean => {
  const hasTextAnimations = state.texts.some((t) => t.animation && t.animation !== 'none');
  const hasLogoAnimation = state.logoProps?.animation !== 'none' && !!state.logoImage;
  const hasImageAnimations = state.canvasImages.some(
    (img) => img.animation && img.animation !== 'none' && !img.deleted
  );

  return hasTextAnimations || hasLogoAnimation || hasImageAnimations;
};

export const saveCanvasAsMedia = async (
  title = 'Canvas Thumbnail',
  format = 'png',
  existingId?: string
): Promise<boolean> => {
  const validFormats = ['png', 'jpeg', 'gif', 'webp'];
  if (!validFormats.includes(format)) {
    toast.error(`Invalid format: ${format}`);
    return false;
  }

  const state = useCanvasStore.getState();
  const currentState = {
    previewMode: state.previewMode,
    selectedId: state.selectedId,
    editingTextId: state.isEditingTextId,
  };

  try {
    useCanvasStore.setState({
      selectedId: null,
      isEditingTextId: null,
    });

    await new Promise((resolve) => setTimeout(resolve, 150));

    const stage = useCanvasStore.getState().stageRef.current;
    if (!stage) return false;

    stage.batchDraw();

    let file: File | null;

    if (format === 'gif' && hasCanvasAnimations(useCanvasStore.getState())) {
      try {
        const blob = await generateCanvasGif(stage);
        if (blob && blob.size > 0) {
          file = new File([blob], `${title}.gif`, { type: 'image/gif' });
        } else {
          throw new Error('Generated GIF was empty');
        }
      } catch (gifError) {
        toast.error('Failed to create animated GIF, using static image');
        const dataURL = stage.toDataURL();
        file = dataURLtoFile(dataURL, `${title}.png`);
        if (!file) return false;
      }
    } else {
      const originalPreviewMode = useCanvasStore.getState().previewMode;

      useCanvasStore.setState({ previewMode: false });

      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        const dataURL = stage.toDataURL({
          mimeType: `image/${format}`,
          quality: format === 'jpeg' ? 0.95 : undefined,
          pixelRatio: 2,
        });

        file = dataURLtoFile(dataURL, `${title}.${format}`);
      } catch (error) {
        toast.error(`Error creating ${format.toUpperCase()} image`);
        return false;
      } finally {
        useCanvasStore.setState({ previewMode: originalPreviewMode });
      }

      if (!file) return false;
    }

    const canvasState = serializeCanvasState();
    const safeTitle = title.replace(/[<>]/g, '');
    const description = `ZNGLY_CANVAS_EDITOR_STATE=${canvasState}`;

    return new Promise((resolve) => {
      createMediaItemMutation(
        {
          file,
          title: safeTitle,
          ...(existingId ? { id: existingId } : {}),
          description,
          altText: safeTitle,
        },
        [],
        (response, errors) => {
          if (errors?.length) {
            toast.error(`Error saving image: ${errors.map((e) => e.message).join(', ')}`);
            resolve(false);
            return;
          }

          if (!response?.newMediaItem?.mediaItem) {
            toast.error('Failed to save image');
            resolve(false);
            return;
          }

          toast.success('Image saved successfully!');
          resolve(true);
        }
      );
    });
  } catch (error) {
    toast.error('Failed to save image');
    return false;
  } finally {
    useCanvasStore.setState({
      previewMode: currentState.previewMode,
      selectedId: currentState.selectedId,
      isEditingTextId: currentState.editingTextId,
    });
  }
};

export const isCanvasThumbnail = (mediaItem: CanvasMediaItemInput | MediaItem): boolean => {
  if (!mediaItem?.description) return false;

  const containsEditorState = ['ZNGLY_CANVAS_EDITOR_STATE=', 'ZNGLY_CANVAS_STATE=', '"isZnglyCanvas":true'].some(
    (marker) => mediaItem.description?.includes(marker) ?? false
  );

  if (containsEditorState) return true;

  return typeof mediaItem?.title === 'string' && mediaItem.title.toLowerCase().includes('canvas thumbnail');
};

const decodeHtmlEntities = (input: string): string => {
  try {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent || input;
  } catch (e) {
    return input;
  }
};

const stripHtmlTags = (html: string): string => {
  return html.replace(/<\/?[^>]+(>|$)/g, '');
};

const fixWordPressJsonEncoding = (jsonStr: string): string => {
  return jsonStr
    .replace(/[\u201C\u201D\u2033\u201E\u2036]/g, '"')
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...');
};

export const loadCanvasStateFromMedia = (mediaItem: CanvasMediaItemInput | MediaItem): boolean => {
  if (!mediaItem?.description) return false;

  try {
    const description = mediaItem.description;
    let jsonStr = '';
    let success = false;

    if (description.includes('ZNGLY_CANVAS_EDITOR_STATE=')) {
      const startIndex = description.indexOf('ZNGLY_CANVAS_EDITOR_STATE=') + 'ZNGLY_CANVAS_EDITOR_STATE='.length;
      jsonStr = description.substring(startIndex);
      jsonStr = stripHtmlTags(jsonStr);
      jsonStr = decodeHtmlEntities(jsonStr);
      jsonStr = fixWordPressJsonEncoding(jsonStr);

      try {
        const stateData = JSON.parse(jsonStr);
        success = applyCanvasData(stateData);
        if (success) return true;
      } catch (error) {
        try {
          const cleanJsonStr = jsonStr.replace(/[^\x20-\x7E]/g, '');
          const stateData = JSON.parse(cleanJsonStr);
          success = applyCanvasData(stateData);
          if (success) return true;
        } catch (e) {
          // Continue to next format
        }
      }
    }

    if (description.includes('ZNGLY_CANVAS_STATE=')) {
      const startIndex = description.indexOf('ZNGLY_CANVAS_STATE=') + 'ZNGLY_CANVAS_STATE='.length;
      jsonStr = description.substring(startIndex);
      try {
        const stateData = JSON.parse(jsonStr);
        success = applyCanvasData(stateData);
        if (success) return true;
      } catch (e) {
        // Continue to next format
      }
    }

    if (description.includes('"isZnglyCanvas":true')) {
      const matches = description.match(/(\{.*\})/);
      if (matches?.[0]) {
        try {
          const jsonData = JSON.parse(matches[0]);
          const canvasData = jsonData.canvasData || jsonData;
          success = applyCanvasData(canvasData);
          if (success) return true;
        } catch (e) {
          // Last format attempt failed
        }
      }
    }

    return success;
  } catch (error) {
    return false;
  }
};

const applyCanvasData = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;

  try {
    const typedData = data as Partial<CanvasState>;
    const store = useCanvasStore.getState();
    store.resetCanvas();

    if (typedData.backgroundColor && typeof typedData.backgroundColor === 'string') {
      store.setBackgroundColor(typedData.backgroundColor);
    }

    if (typedData.bgImage && typeof typedData.bgImage === 'string') {
      store.setBgImage(typedData.bgImage);
    }

    if (Array.isArray(typedData.texts)) {
      const validTexts = typedData.texts
        .filter((text) => typeof text === 'object' && text && 'id' in text)
        .map((text) => ({
          ...text,
          align: ['left', 'center', 'right'].includes(text.align as string)
            ? (text.align as 'left' | 'center' | 'right')
            : 'left',
          animation: typeof text.animation === 'string' ? (text.animation as AnimationType) : 'none',
          animationDuration: text.animationDuration || 1,
          animationDelay: text.animationDelay || 0,
          zIndex: text.zIndex || 1,
        })) as unknown as TextItem[];

      store.setTexts(validTexts);
    }

    if (Array.isArray(typedData.images) && typedData.images.length > 0) {
      typedData.images.forEach((img) => {
        if (img && typeof img.url === 'string') {
          const id = store.addImage(img.url);

          if (id) {
            store.updateImage(id, {
              x: typeof img.x === 'number' ? img.x : 0,
              y: typeof img.y === 'number' ? img.y : 0,
              width: typeof img.width === 'number' ? img.width : 200,
              height: typeof img.height === 'number' ? img.height : 200,
              opacity: typeof img.opacity === 'number' ? img.opacity : 1,
              rotation: typeof img.rotation === 'number' ? img.rotation : 0,
              animation: typeof img.animation === 'string' ? (img.animation as AnimationType) : 'none',
              zIndex: typeof img.zIndex === 'number' ? img.zIndex : 1,
            });
          }
        }
      });
    }

    if (
      typedData.logoImage &&
      typeof typedData.logoImage === 'string' &&
      typedData.logoProps &&
      typeof typedData.logoProps === 'object'
    ) {
      store.setLogoImage(typedData.logoImage);
      const props = typedData.logoProps as Partial<LogoProps>;

      if (
        typeof props.x === 'number' &&
        typeof props.y === 'number' &&
        typeof props.width === 'number' &&
        typeof props.height === 'number'
      ) {
        const validLogoProps: LogoProps = {
          x: props.x,
          y: props.y,
          width: props.width,
          height: props.height,
          animation: typeof props.animation === 'string' ? (props.animation as AnimationType) : 'none',
        };

        store.setLogoProps(validLogoProps);
      }
    }

    return true;
  } catch (e) {
    return false;
  }
};
